import { useCallback, useEffect, useState } from 'react'
import { logError } from '@/utils/errorUtils'
import type { OrpcClient } from '@/orpc/client'
import { useClient } from './useClient'

// Deterministic clinical-summary dataset served by GET /v2/patient/{id}/clinical-summary-details.
export type ClinicalSummaryDetailsData = Awaited<
  ReturnType<OrpcClient['patientClinicalSummaryDetails']>
>

export function usePatientClinicalSummaryDetails(patientId: string | undefined) {
  const client = useClient()
  const [data, setData] = useState<ClinicalSummaryDetailsData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!patientId) {
      setData(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const r = await client.patientClinicalSummaryDetails({ id: patientId })
      setData(r)
    } catch (err) {
      logError('patient', 'Failed to load clinical summary', err)
      setError('Failed to load clinical summary.')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [client, patientId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { data, loading, error, refresh }
}
